import { CarePlanQolGoalResponse } from '~/lib/types/carePlan/carePlans';
import {
  ChakraFlex,
  ChakraGrid,
  FAIconName,
  FireflyKebabMenu,
  FireflyKebabMenuItem,
  TagDropdown,
  Text,
  useToastMessage,
} from '@equip.health/ui';
import {
  buildGoalStatusTagDropdownProps,
  getCarePlanGoalTagDropdownOptions,
} from '~/components/maud/patients/profile/tabs/careplan/CarePlanGoalTagDropdownOptions';
import { useUpdateQoLGoalMutation } from '~/store/services/carePlan';
import { useEffect, useMemo } from 'react';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import { CarePlanDescriptionRow } from './CarePlanDescriptionRow';
import { useFlags } from 'launchdarkly-react-client-sdk';

type Props = {
  qolGoal: CarePlanQolGoalResponse;
  onEditClick: (qolGoal: CarePlanQolGoalResponse) => void;
  onRemoveClick: (qolGoalId: string) => void;
  treatmentEpisodeId: string;
};
export const CarePlanQolGoalCard = ({
  qolGoal,
  onEditClick,
  onRemoveClick,
  treatmentEpisodeId,
}: Props) => {
  const {
    carePlanNotesDisplay: isCarePlanNotesDisplayEnabled,
    carePlanM4MedPsychProblems: isMedPsychProblemsEnabled,
  } = useFlags<LDFlags>();
  const [updateQoLGoal, { error }] = useUpdateQoLGoalMutation();
  const { createToast } = useToastMessage();

  const statusDropdownOptions = useMemo(
    () => getCarePlanGoalTagDropdownOptions(isMedPsychProblemsEnabled),
    [isMedPsychProblemsEnabled],
  );

  const isDescriptionPresent = Boolean(
    qolGoal.descriptionDetails?.description?.trim(),
  );

  useEffect(() => {
    if (error) {
      createToast({
        id: 'qol-goal-card-update-error',
        text: "An error occurred updating the goal's status. Please try again later.",
        variant: 'critical',
      });
    }
  }, [error]);

  const kebabMenuOptions: FireflyKebabMenuItem[] = [
    {
      label: 'Edit goal',
      leftIcon: FAIconName.penRegular,
      onClick: () => onEditClick(qolGoal),
    },
    {
      label: 'Remove goal',
      leftIcon: FAIconName.trashRegular,
      leftIconColor: 'critical.base',
      onClick: () => {
        onRemoveClick(qolGoal.id);
      },
    },
  ];

  return (
    <ChakraFlex
      borderColor="neutral.line"
      borderRadius="12px"
      borderWidth="1px"
      direction="column"
      gap="6px"
      padding="18px"
    >
      <ChakraGrid
        columnGap="12px"
        templateColumns="1fr auto auto"
        templateRows="1fr"
        width="100%"
      >
        <Text variant="h5">{qolGoal.title}</Text>
        <TagDropdown
          id={`care-plan-qol-goal-${qolGoal.status.code}-status-dropdown`}
          menuOptions={statusDropdownOptions}
          onOptionClick={(newStatus) => {
            updateQoLGoal({
              goalId: qolGoal.id,
              treatmentEpisodeId,
              body: {
                description: qolGoal.description,
                statusCode: newStatus as CarePlanGoalStatus,
                title: qolGoal.title,
              },
            });
          }}
          {...buildGoalStatusTagDropdownProps(
            qolGoal.status.code,
            qolGoal.status.label,
            statusDropdownOptions,
          )}
        />
        <FireflyKebabMenu
          aria-label={`Options menu for ${qolGoal.title}`}
          options={kebabMenuOptions}
        />
      </ChakraGrid>
      {isCarePlanNotesDisplayEnabled && isDescriptionPresent ? (
        <CarePlanDescriptionRow
          descriptionDetails={qolGoal.descriptionDetails}
          goalId={qolGoal.id}
        />
      ) : (
        <Text>{qolGoal.description}</Text>
      )}
    </ChakraFlex>
  );
};
